import { useLayoutEffect, useRef } from 'react';
import { hashKey } from '@tanstack/react-query';

export function obsoletePerformancePreparation() {
    return Object.assign(new Error('Performance preparation was superseded.'), { code: 'performance_preparation_obsolete' });
}

export function assertPerformancePreparationCurrent(isCurrent) {
    if (typeof isCurrent !== 'function' || !isCurrent()) throw obsoletePerformancePreparation();
}

// Pausing a query must not erase the accepted frame it already displayed.
export function useFrozenPerformanceValue(query, preparing, scopeKey = null) {
    const accepted = useRef({ scopeKey, query });
    if (!preparing || accepted.current.scopeKey !== scopeKey) accepted.current = { scopeKey, query };
    return preparing ? accepted.current.query : query;
}

export function usePerformancePreparationRegistration(register, prepare, stamp) {
    const latest = useRef({ prepare, stamp, active: true, registration: null, epoch: 0, pending: new Set() });
    if (latest.current.stamp !== stamp) {
        latest.current.epoch += 1;
        latest.current.pending.forEach(retire => retire());
        latest.current.pending.clear();
    }
    latest.current.prepare = prepare; latest.current.stamp = stamp;
    useLayoutEffect(() => {
        if (typeof register !== 'function') return undefined;
        latest.current.active = true;
        const registration = {}; latest.current.registration = registration;
        const capability = options => {
            const prepare = latest.current.prepare; const stamp = latest.current.stamp; const epoch = latest.current.epoch;
            const publicationCurrent = () => latest.current.active && latest.current.registration === registration && options.isCurrentRequest();
            const current = () => publicationCurrent() && latest.current.epoch === epoch && latest.current.stamp === stamp;
            let retire;
            const retired = new Promise((_, reject) => { retire = () => reject(obsoletePerformancePreparation()); });
            latest.current.pending.add(retire);
            const operation = (async () => {
                assertPerformancePreparationCurrent(current);
                const frame = await prepare({ ...options, isCurrentRequest: current, isPublicationCurrent: publicationCurrent });
                assertPerformancePreparationCurrent(current);
                if (typeof frame.commit === 'function') return frame;
                const frameCurrent = frame.canCommit;
                return { ...frame, canCommit: () => current() && (typeof frameCurrent !== 'function' || frameCurrent()) };
            })();
            const remove = () => latest.current.pending.delete(retire);
            operation.then(remove, remove);
            return Promise.race([operation, retired]);
        };
        register(capability);
        return () => {
            latest.current.active = false; latest.current.registration = null;
            latest.current.pending.forEach(retire => retire());
            latest.current.pending.clear(); register(null);
        };
    }, [register]);
    return latest;
}

export async function preparePerformanceQuery(client, queryKey, queryFn, isCurrentRequest, options = {}) {
    assertPerformancePreparationCurrent(isCurrentRequest);
    const data = await client.fetchQuery({ queryKey, queryFn, retry: false, staleTime: 300_000, ...options });
    assertPerformancePreparationCurrent(isCurrentRequest);
    return data;
}

export function nextPerformanceProps(props, target) {
    const context = target?.canonicalFactorContext;
    const versions = target?.versions;
    if (target?.watchlistIsReady !== false && (!context?.vendor || !context?.factor_release_id || !context?.factor_as_of_date)
        || !Number.isSafeInteger(versions?.watchlistVersion) || !Number.isSafeInteger(versions?.actualPortfolioVersion)
        || typeof target.actualPortfolioIsReady !== 'boolean' || typeof target.watchlistIsReady !== 'boolean') {
        throw new Error('Performance requires the exact prepared factor context, versions and readiness.');
    }
    return { ...props, factorContext: context, watchlistVersion: versions.watchlistVersion,
        actualPortfolioVersion: versions.actualPortfolioVersion,
        actualPortfolioIsReady: target.actualPortfolioIsReady, watchlistIsReady: target.watchlistIsReady,
        initialContextPending: false, initialContextFailed: false };
}

export function performancePreparationStamp(value) { return hashKey(value); }
